import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ImageOptimizationService {
  private imageCache = new Map<string, HTMLImageElement>();
  private preloadedImages = new Set<string>();

  /**
   * Pré-carrega uma imagem e a armazena em cache
   */
  preloadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      if (this.imageCache.has(src)) {
        resolve(this.imageCache.get(src)!);
        return;
      }

      const img = new Image();
      img.onload = () => {
        this.imageCache.set(src, img);
        this.preloadedImages.add(src);
        resolve(img);
      };
      img.onerror = () => {
        reject(new Error(`Falha ao carregar imagem: ${src}`));
      };
      img.src = src;
    });
  }

  /**
   * Pré-carrega múltiplas imagens em paralelo
   */
  preloadImages(sources: string[]): Promise<HTMLImageElement[]> {
    const promises = sources.map(src => this.preloadImage(src));
    return Promise.all(promises);
  }

  /**
   * Verifica se uma imagem já foi pré-carregada
   */
  isPreloaded(src: string): boolean {
    return this.preloadedImages.has(src);
  }

  /**
   * Obtém uma imagem do cache
   */
  getCachedImage(src: string): HTMLImageElement | null {
    return this.imageCache.get(src) || null;
  }

  /**
   * Limpa o cache de imagens
   */
  clearCache(): void {
    this.imageCache.clear();
    this.preloadedImages.clear();
  }

  /**
   * Otimiza o carregamento de imagens críticas da aplicação
   */
  preloadCriticalImages(): void {
    const criticalImages = [
      '/assets/logo/PNG/simbolo/amareloLogo.png',
      '/assets/logo/PNG/assinaturaVisual/amarelo.png'
    ];

    this.preloadImages(criticalImages).catch(error => {
      console.warn('Erro ao pré-carregar imagens críticas:', error);
    });
  }

  /**
   * Aplica lazy loading em imagens
   */
  applyLazyLoading(): void {
    const images = document.querySelectorAll('img[data-src]');
    
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.getAttribute('data-src');
          
          if (src) {
            img.src = src;
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }

  /**
   * Otimiza o tamanho de imagens baseado no viewport
   */
  getOptimizedImageSrc(originalSrc: string, width?: number): string {
    if (width && width <= 768) {
      return originalSrc.replace('.png', '-small.png');
    }
    return originalSrc;
  }
} 
